import React, { useState, useRef, useEffect } from 'react';
import { Upload, CircleCheck as CheckCircle, CircleAlert as AlertCircle, Loader, FileText, Video, Image, X, Info } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { UploadedDocument } from './types';

const MIN_FILES = 3;
const MAX_FILE_MB = 100;

interface UploadedFile {
  file: File;
  url: string;
  uploading: boolean;
  error?: string;
}

const CLT_CHECKLIST = [
  'Selfie com documento original (RG ou CNH)',
  'Vídeo entrando e saindo de casa falando o nome da vendedora',
  'Carteira de Trabalho exportada do app (hoje)',
  'Foto RG ou CNH — frente',
  'Foto RG ou CNH — verso',
  '1°, 2° e 3° Holerites recentes',
  'Extrato bancário — 3 meses',
  'Comprovante de endereço em nome do cliente',
  'Foto do local de trabalho',
  'Foto da frente da residência',
  'Print do perfil do Instagram',
  'Print do perfil do Facebook',
  'Print da conversa com cada contato adicional cadastrado',
];

const AUTONOMOUS_CHECKLIST = [
  'Selfie com documento original (RG ou CNH)',
  'Vídeo entrando e saindo de casa falando o nome da vendedora',
  'Foto RG ou CNH — frente',
  'Foto RG ou CNH — verso',
  'Extrato bancário — 3 meses',
  'Comprovante de endereço em nome do cliente',
  'Foto do local de trabalho / comércio',
  'Foto da frente da residência',
  'Print do perfil do Instagram',
  'Print do perfil do Facebook',
  'Print da conversa com cada contato adicional cadastrado',
];

interface Props {
  documents: UploadedDocument[];
  contractTempId: string;
  onComplete: (documents: UploadedDocument[]) => void;
  onChange?: (documents: UploadedDocument[]) => void;
  clientType?: 'clt' | 'autonomous';
}

function toDocuments(files: UploadedFile[]): UploadedDocument[] {
  const done = files.filter(f => f.url && !f.uploading);
  return [{
    category: 'general',
    label: 'Documentos',
    files: done.map(f => f.file),
    urls: done.map(f => f.url),
    uploading: false,
  }];
}

function fileIcon(file: File) {
  if (file.type.startsWith('video/')) return <Video className="w-3.5 h-3.5 flex-shrink-0 text-blue-500" />;
  if (file.type.startsWith('image/')) return <Image className="w-3.5 h-3.5 flex-shrink-0 text-emerald-500" />;
  return <FileText className="w-3.5 h-3.5 flex-shrink-0 text-gray-500" />;
}

export default function DocumentsStep({ documents: initialDocs, contractTempId, onComplete, onChange, clientType }: Props) {
  const [files, setFiles] = useState<UploadedFile[]>(() =>
    initialDocs
      .filter(d => d.category === 'general')
      .flatMap(d => d.urls.map((url, i) => ({ file: d.files[i] || new File([], `arquivo-${i + 1}`), url, uploading: false }))),
  );

  useEffect(() => {
    onChange?.(toDocuments(files));
  }, [files, onChange]);
  const [dragOver, setDragOver] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const checklist = clientType === 'autonomous' ? AUTONOMOUS_CHECKLIST : CLT_CHECKLIST;
  const uploadedCount = files.filter(f => f.url && !f.uploading).length;

  const uploadFile = async (file: File) => {
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setFiles(prev => [...prev, { file, url: '', uploading: false, error: `Arquivo muito grande. Máx ${MAX_FILE_MB}MB.` }]);
      return;
    }

    const tempEntry: UploadedFile = { file, url: '', uploading: true };
    setFiles(prev => [...prev, tempEntry]);

    try {
      const ext = file.name.split('.').pop();
      const filePath = `manual/${contractTempId}/doc-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('contract-files')
        .upload(filePath, file, { contentType: file.type, upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('contract-files').getPublicUrl(filePath);

      setFiles(prev => prev.map(f =>
        f === tempEntry ? { ...f, url: urlData.publicUrl, uploading: false } : f
      ));
    } catch (err) {
      console.error('Upload error:', err);
      setFiles(prev => prev.map(f =>
        f === tempEntry ? { ...f, uploading: false, error: 'Erro no upload. Tente novamente.' } : f
      ));
    }
  };

  const handleFileSelection = (fileList: FileList | null) => {
    if (!fileList) return;
    setGlobalError('');
    Array.from(fileList).forEach(uploadFile);
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleContinue = () => {
    if (uploadedCount < MIN_FILES) {
      setGlobalError(`Anexe ao menos ${MIN_FILES} documentos para continuar.`);
      return;
    }
    setGlobalError('');
    onComplete(toDocuments(files));
  };

  const anyUploading = files.some(f => f.uploading);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Documentos</h2>
        <p className="text-gray-600 text-sm">
          Anexe todos os documentos do cliente em um único envio. Fotos, vídeos e PDFs são aceitos.
        </p>
      </div>

      {/* Checklist informativo */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <p className="text-sm font-semibold text-blue-800">Lembre-se de pedir ao cliente:</p>
        </div>
        <ul className="space-y-1.5">
          {checklist.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-blue-700">
              <span className="w-4 h-4 flex-shrink-0 mt-0.5 rounded-full bg-blue-200 text-blue-700 flex items-center justify-center text-xs font-bold">
                {i + 1}
              </span>
              {item}
            </li>
          ))}
        </ul>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFileSelection(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
          dragOver
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,video/*,application/pdf,.pdf"
          className="hidden"
          onChange={(e) => { handleFileSelection(e.target.files); e.target.value = ''; }}
        />
        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
        <p className="text-sm font-semibold text-gray-700 mb-1">
          Clique ou arraste os arquivos aqui
        </p>
        <p className="text-xs text-gray-500">
          Fotos, vídeos e PDFs — até {MAX_FILE_MB}MB por arquivo
        </p>
      </div>

      {/* Progress indicator */}
      {files.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-medium text-gray-700">
              {uploadedCount} arquivo{uploadedCount !== 1 ? 's' : ''} enviado{uploadedCount !== 1 ? 's' : ''}
            </span>
            <span className={`text-sm font-semibold ${uploadedCount >= MIN_FILES ? 'text-emerald-600' : 'text-amber-600'}`}>
              {uploadedCount >= MIN_FILES ? 'Mínimo atingido' : `Mínimo: ${MIN_FILES}`}
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${uploadedCount >= MIN_FILES ? 'bg-emerald-500' : 'bg-blue-500'}`}
              style={{ width: `${Math.min((uploadedCount / MIN_FILES) * 100, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((f, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border text-sm ${
                f.error
                  ? 'border-red-200 bg-red-50'
                  : f.uploading
                  ? 'border-gray-200 bg-gray-50'
                  : 'border-emerald-200 bg-emerald-50'
              }`}
            >
              <div className="flex-shrink-0">
                {f.uploading
                  ? <Loader className="w-4 h-4 text-gray-400 animate-spin" />
                  : f.error
                  ? <AlertCircle className="w-4 h-4 text-red-500" />
                  : <CheckCircle className="w-4 h-4 text-emerald-500" />
                }
              </div>
              {fileIcon(f.file)}
              <span className={`flex-1 truncate text-xs font-medium ${f.error ? 'text-red-700' : f.uploading ? 'text-gray-600' : 'text-emerald-800'}`}>
                {f.file.name}
              </span>
              {f.error && (
                <span className="text-xs text-red-600 flex-shrink-0">{f.error}</span>
              )}
              {!f.uploading && (
                <button
                  onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                  className="flex-shrink-0 p-1 rounded hover:bg-red-100 hover:text-red-600 transition-colors text-gray-400"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {globalError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {globalError}
        </div>
      )}

      {uploadedCount < MIN_FILES && files.length === 0 && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          Envie ao menos {MIN_FILES} documentos para continuar.
        </div>
      )}

      <button
        onClick={handleContinue}
        disabled={anyUploading}
        className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold text-base hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {anyUploading ? 'Enviando...' : 'Continuar'}
      </button>
    </div>
  );
}
